import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RouteComponentProps } from "@reach/router";

import { useCompanyPreviewData } from "../hooks/useCompanyPreviewData";
import { DocumentCategoryGroup } from "./DocumentCategoryGroup";
import { getDocumentCategories } from "actions/companyActions";
import type { RootState } from "newui/store";
import type { CorporateDocument } from "newui/features/Company/types";

type CorporateDocumentsTabProps = RouteComponentProps;

interface DocumentCategory {
	id: number;
	name: string;
	parent_id: number;
}

interface DocumentsByCategory {
	categoryId: number;
	categoryName: string;
	documents: CorporateDocument[];
}

export const CorporateDocumentsTab: React.FC<
	CorporateDocumentsTabProps
> = () => {
	const dispatch = useDispatch();
	const { documents, profileTypeId } = useCompanyPreviewData();

	const documentCategories = useSelector(
		(state: RootState) =>
			((state.companyProfile.documentCategories as DocumentCategory[]) ?? [])
				.filter((c) => c.parent_id === 0)
	);

	useEffect(() => {
		if (profileTypeId) {
			(dispatch as any)(getDocumentCategories(profileTypeId));
		}
	}, [dispatch, profileTypeId]);

	const grouped = useMemo((): DocumentsByCategory[] => {
		if (!documentCategories.length) return [];

		return documentCategories
			.map((category) => {
				const categoryDocuments = documents.filter(
					(doc) =>
						doc.category &&
						(doc.category.id === category.id ||
							doc.category.parent_id === category.id),
				);

				return {
					categoryId: category.id,
					categoryName: category.name,
					documents: categoryDocuments,
				};
			})
			.filter((group) => group.documents.length > 0);
	}, [documents, documentCategories]);

	if (documents.length === 0) {
		return (
			<p className="py-8 text-center text-sm text-gray-500">
				No corporate documents available.
			</p>
		);
	}

	return (
		<div className="space-y-6">
			{grouped.map((group) => (
				<DocumentCategoryGroup
					key={group.categoryId}
					categoryId={group.categoryId}
					title={group.categoryName}
					documents={group.documents}
				/>
			))}
		</div>
	);
};
